import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./GeoipModal.module.scss";
import {
  Overlay,
  Heading,
  Button,
  ButtonGroup,
  Text,
  Prose
} from "../elements";
import {
  geoIpCheck,
  waitForIvid,
  isAlreadyOnPreferredSite,
  isSafeRedirectPath,
  getSavedGeoPreference,
  saveGeoPreference,
  getIvid
} from "./utils";
import classNames from "classnames/bind";

const cx = classNames.bind(styles);
const DEFAULTS = {
  heading: "Welcome to Intuit",
  subheadingMsg1:
    "To check out products designed specifically for your country, please visit the",
  subheadingMsg2: "site.",
  tracking: {
    "data-action": "interacted",
    "data-object": "content",
    "data-ui-action": "clicked",
    "data-ui-object": "link",
    "data-ui-object-detail": "Take me there",
    "data-ui-access-point": "",
    "data-sku": ""
  },
  noThankstracking: {
    "data-ui-object-detail": "Visit the site"
  }
};

/**
 * GEOIP modal to route a user to country specific page
 */
const GeoipModal = ({
  heading = DEFAULTS.heading,
  subheadingMsg1 = DEFAULTS.subheadingMsg1,
  subheadingMsg2 = DEFAULTS.subheadingMsg2,
  noThankstracking = DEFAULTS.noThankstracking,
  tracking = DEFAULTS.tracking
}) => {
  // geo data is managed as React state so the component never reads from the
  // mutable module-level geoData singleton at render time (fixes concurrent-mode
  // staleness and test-isolation issues).
  const [geoModalData, setGeoModalData] = useState(null);
  const ividRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    waitForIvid((ivid) => {
      if (!mountedRef.current) return;
      ividRef.current = ivid;
      const savedPath = getSavedGeoPreference(ivid);
      // Only honour the stored preference when it is a safe relative path.
      // Absolute or unsafe values (e.g. injected javascript: or cross-origin URLs)
      // are silently ignored so the normal geo-check flow runs instead.
      if (savedPath && isSafeRedirectPath(savedPath)) {
        if (!isAlreadyOnPreferredSite(savedPath)) {
          window.location.href = savedPath;
        }
        return;
      }
      const data = geoIpCheck();
      if (data) {
        setGeoModalData(data);
      }
    });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!geoModalData) return null;
  // Rendered via a portal to document.body so the modal lives outside the
  if (typeof document === "undefined") return null;

  // When waitForIvid timed out (ivid was null at callback time) but the
  // getSameSiteAttributeScript has since run and set the cookie, we re-read
  // the cookie at click-time so the preference is never silently dropped.
  const getEffectiveIvid = () => ividRef.current !== null ? ividRef.current : getIvid();

  return ReactDOM.createPortal(
    <>
      <Overlay />
      <div className={cx(geoModalData.modalShow)}>
        <div className={cx("geo-ip-redirect-popup")}>
          <Prose align="center">
            <Heading node="h2">{heading}</Heading>
            <Text>
              {subheadingMsg1} {geoModalData.countryName} {subheadingMsg2}
            </Text>
            <ButtonGroup isCentered>
              <Button
                tabindex="1"
                onClick={() => {
                  saveGeoPreference(getEffectiveIvid(), geoModalData.regionSitePath);
                  setGeoModalData(null);
                }}
                pos={1}
                variant="plain"
                className={cx("no-thanks")}
                isLink="true"
                tracking={geoModalData.noThanksDataWa}
                data-action={tracking["data-action"]}
                data-object={tracking["data-object"]}
                data-ui-action={tracking["data-ui-action"]}
                data-ui-object={tracking["data-ui-object"]}
                data-ui-access-point={tracking["data-ui-access-point"]}
                data-ui-object-detail={
                  noThankstracking["data-ui-object-detail"]
                }
              >
                <span>Visit the {geoModalData.regionCode} site</span>
              </Button>
              <Button
                tabindex="1"
                isLink="true"
                pos={2}
                onClick={() => {
                  saveGeoPreference(getEffectiveIvid(), geoModalData.geoPageUrl);
                }}
                href={geoModalData.geoPageUrl}
                tracking={geoModalData.visitSiteDataWa}
                data-action={tracking["data-action"]}
                data-object={tracking["data-object"]}
                data-ui-action={tracking["data-ui-action"]}
                data-ui-object={tracking["data-ui-object"]}
                data-ui-access-point={tracking["data-ui-access-point"]}
                data-ui-object-detail={tracking["data-ui-object-detail"]}
                className={cx("ctaprimary", "goto-site")}
              >
                <span>Go to {geoModalData.countryCode} site</span>
              </Button>
            </ButtonGroup>
          </Prose>
        </div>
      </div>
    </>,
    document.body
  );
};

export { GeoipModal as default };